/**
 * The page displaying the history of tasks.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2024-2026
 */
import '@/components/styles/Input.css'
import TabContainer from '@/components/TabContainer'
import AimdTaskList from '@/pages/TaskList/AimdTaskList'
import TaskList from '@/pages/TaskList/TaskList'
import { useSelector } from 'react-redux'
import './TaskList.css'

import {
  fetchAimdTaskList,
  fetchAllTaskList,
  fetchCompanyConsumption,
} from '@/api'
import HorizontalProgressBar from '@/components/HorizontalProgressBar'
import { logger } from '@/config/logger'
import { getUserState, store } from '@/store'
import { JobDetail, JobStatus, Util } from '@amplify3/duplo-back-end'
import { useEffect, useRef } from 'react'

export const TaskListPage = () => {
  const selectTabIndex = Number(
    new URLSearchParams(window.location.search).get('selectTab')
  )

  const { aimdTaskList, lastAimdTaskId, jobList, lastJobId } =
    useSelector(getUserState)
  // `refreshingRef` is the actual guard that prevents two refreshes
  // from running at the same time (e.g. a user click while the
  // automatic loop is already mid-refresh). It must be a ref, not
  // state: a ref's value is always up to date immediately, even
  // inside async code, whereas a state value read inside an already
  // running function stays frozen at whatever it was when that
  // function started (a "stale closure").
  const refreshingRef = useRef(false)

  // Remembers when the automatic refresh loop started, so we can
  // slow down the polling frequency the longer a task keeps running
  // (see refreshDelay below).
  const startTime = useRef(Date.now())

  // Tracks whether this page is still on screen. The refresh loop
  // below calls itself repeatedly (refresh, wait, refresh, wait...).
  // If the user navigates away, the component is "unmounted" but the
  // loop, being just a chain of promises, would otherwise keep running
  // forever in the background. We check this flag before continuing
  // the loop so it stops as soon as the page is left.
  const mountedRef = useRef(true)

  // Runs once when the page first appears ("mounts"): kicks off the
  // first refresh. The function returned here is React's built-in
  // way to run cleanup when the page disappears ("unmounts") — there
  // is no separate "onUnmount" hook, this is it.
  useEffect(() => {
    mountedRef.current = true
    refreshJobList()

    return () => {
      mountedRef.current = false
    }
  }, [])

  function isRunningJobList(jobList: JobDetail[]) {
    if (jobList) {
      for (const job of jobList) {
        if (
          job.job_status != JobStatus.Success &&
          job.job_status != JobStatus.Error
        ) {
          return true
        }
      }
    }

    return false
  }

  // Fetches both task lists, then decides whether to schedule another
  // refresh. This is also what runs the "auto-refresh loop": as long
  // as a task is still running, it waits a bit and calls itself again
  // (recursion instead of setInterval, so refreshes never overlap).
  //
  // fetchAimdTaskList/fetchAllTaskList update the Redux store *before*
  // their returned promise resolves, so once both `await`s below
  // finish, the store already holds the fresh lists — we don't need
  // to wait for React to re-render to read them.
  const refreshJobList = async () => {
    // Ignore this call if a refresh is already in flight (either the
    // auto-loop or a manual click on the refresh button).
    if (refreshingRef.current) {
      logger.debug('Refresh running: ignore this request.')
      return
    }

    refreshingRef.current = true
    logger.debug('Request refreshing task list.')

    // Fire both requests together and wait until both are done.
    await Promise.all([
      fetchAimdTaskList({ refreshing: true }),
      fetchAllTaskList({ refreshing: true }),
    ])

    refreshingRef.current = false
    if (!mountedRef.current) {
      // The page was left while we were fetching: stop here instead
      // of touching state on a page that isn't shown anymore, and
      // don't schedule another refresh.
      return
    }

    logger.debug('List refresh done.')

    // Read the just-updated lists straight from the store (see the
    // comment above the function) to decide if a task is still
    // running and another refresh is needed.
    const { jobList, aimdTaskList } = store.getState().user
    if (
      (jobList && isRunningJobList(jobList)) ||
      (aimdTaskList && isRunningJobList(aimdTaskList))
    ) {
      // The longer a task has been running, the less often we poll,
      // to avoid hammering the back-end for a task that takes a while.
      const runningTime = Date.now() - startTime.current
      const refreshDelay =
        runningTime > 120_000 ? 20_000 : runningTime > 30_000 ? 10_000 : 3_000
      logger.debug(
        `Running task exists since ${runningTime / 1000} s.`,
        `Request refresh in ${refreshDelay / 1000} s...`
      )
      await Util.sleep(refreshDelay)
      if (mountedRef.current) {
        // Still on this page after the wait: loop by calling
        // ourselves again.
        refreshJobList()
      }
    } else {
      logger.debug('All task completed.')
      fetchCompanyConsumption({ forceRefresh: true })
    }
  }

  return (
    <div>
      {!aimdTaskList || !jobList ? (
        <HorizontalProgressBar />
      ) : (
        <div className="jobui-globalContainer page">
          <TabContainer
            tabList={['AI Music Detector', 'All Tasks']}
            selectTabIndex={selectTabIndex}
          >
            <AimdTaskList
              taskList={aimdTaskList ?? []}
              lastTaskId={lastAimdTaskId}
              requestMoreTasks={() => fetchAimdTaskList({ refreshing: false })}
            />
            <TaskList
              taskList={jobList ?? []}
              lastTaskId={lastJobId}
              requestMoreTasks={() => fetchAllTaskList({ refreshing: false })}
            />
          </TabContainer>
        </div>
      )}
    </div>
  )
}
